import { ApolloClient, ApolloLink, from } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import Cookies from 'js-cookie'
import {
  handleErrors,
  httpLink,
  requestIDLink,
  retryLink,
  timingLink
} from 'utils/apollo'
import { AppCookie } from 'utils/cookies'
import { transformPreviewDomain } from 'utils/preview'

import { paymentsCache } from './cache'
import { oauth2RefreshPayments } from './refresh'

const paymentsErrorLink = onError(errors =>
  handleErrors(errors, oauth2RefreshPayments)
)

/**
 * Apollo link for customer authentication.
 * Authentication is done via token.
 * X-Customer-Id is used to identify the selected team.
 * X-Origin is used to bypass the Origin in local development.
 *
 * This link also automatically transforms preview domain names to staging domains
 * for backend lookup (e.g., ribeiro.pr-3187.staging... → ribeiro.staging...)
 */
const paymentsAuthLink = new ApolloLink((operation, forward) => {
  const existingHeaders = operation?.getContext()?.headers || {}
  const headers: Record<string, string> = {
    ...existingHeaders
  }

  const token = Cookies.get(AppCookie.TokenCustomer)
  if (token) {
    headers.authorization = `Bearer ${token}`
  }

  const customerId = Cookies.get(AppCookie.CustomerId)
  if (customerId) {
    headers['X-Customer-Id'] = customerId
  }

  // Handle X-Origin header with automatic preview domain transformation
  // This works for:
  // 1. Client-side with env var: Uses NEXT_PUBLIC_PORTAL_DOMAIN
  // 2. SSR: Uses manually set X-Origin from getServerSideProps (req.headers.host)
  // 3. Client-side fallback: Extracts from window.location.hostname
  if (process.env.NEXT_PUBLIC_PORTAL_DOMAIN) {
    headers['X-Origin'] = transformPreviewDomain(
      process.env.NEXT_PUBLIC_PORTAL_DOMAIN
    )
  } else if (existingHeaders['X-Origin']) {
    // Transform X-Origin if already set by SSR code
    headers['X-Origin'] = transformPreviewDomain(existingHeaders['X-Origin'])
  } else if (typeof window !== 'undefined' && window.location?.hostname) {
    // Client-side fallback: extract domain from browser location
    // This handles preview environments where no env var is set
    headers['X-Origin'] = transformPreviewDomain(window.location.hostname)
  }

  const impersonating = Cookies.get(AppCookie.Impersonating)
  if (impersonating) {
    headers['X-Impersonating'] = impersonating
  }

  operation.setContext({ headers })

  return forward(operation)
})

export const paymentsClient = new ApolloClient({
  // order matters here - put errorLink first so the refresh token flow works
  link: from([
    requestIDLink,
    timingLink,
    paymentsErrorLink,
    paymentsAuthLink,
    retryLink,
    httpLink
  ]),
  cache: paymentsCache,
  ssrMode: false,
  defaultOptions: {
    watchQuery: {
      errorPolicy: 'all',
      fetchPolicy: 'network-only',
      nextFetchPolicy: 'cache-and-network'
    },
    query: {
      errorPolicy: 'all'
    }
  }
})
